/**
 * Email theme token contract (Brand / Gray / Dark).
 *
 * Base HTML uses {{THEME.tokenName}} slots (camelCase).
 * Go vars {{.StoreLogo}} etc. stay intact.
 *
 * Prismic Theme custom type → `data.variables` (SCSS) → flat TLP keys
 * (same pipeline as treez-tlp/scripts/theme.js). Missing fields fall back
 * to MOCK_PRISMIC_THEME.
 */

import { getPrismicThemeVariables } from "@/utils";
import {
    BAG_DEFAULTS,
    DECLINED_DEFAULTS,
    MOCK_PRISMIC_THEME,
} from "../constants/themeDefaults";
import { FILL_TOKEN_KEYS, TEXT_TOKEN_KEYS } from "../constants/tokenKeys";

export { MOCK_PRISMIC_THEME };

const t = (theme, key, fallback) => {
    const value = theme?.[key];
    if (value != null && value !== "" && value !== "none") return value;
    const mock = MOCK_PRISMIC_THEME[key];
    if (mock != null && mock !== "") return mock;
    return fallback;
};

const linkTokensFromTheme = theme => {
    const colorLink = t(theme, "label-btn-link", "#2a384e");
    // Wheelhouse (and some TLP themes) typo: `$label-btn-link-hove`
    const colorLinkHover = t(
        theme,
        "label-btn-link-hover",
        t(theme, "label-btn-link-hove", colorLink),
    );
    return { colorLink, colorLinkHover };
};

const bagFromTheme = theme => ({
    bagBaseColor: t(
        theme,
        "cart-empty-bag-base-color",
        BAG_DEFAULTS.bagBaseColor,
    ),
    bagTraceBaseColor: t(
        theme,
        "cart-empty-bag-trace-base-color",
        BAG_DEFAULTS.bagTraceBaseColor,
    ),
    bagSideColor: t(
        theme,
        "cart-empty-bag-side-color",
        BAG_DEFAULTS.bagSideColor,
    ),
    bagLateralStroke: t(
        theme,
        "cart-empty-bag-lateral-stroke",
        BAG_DEFAULTS.bagLateralStroke,
    ),
    bagFrontBag: t(theme, "cart-empty-bag-front-bag", BAG_DEFAULTS.bagFrontBag),
    bagSignal: t(theme, "cart-empty-bag-signal", BAG_DEFAULTS.bagSignal),
    bagShadow: t(theme, "cart-empty-bag-shadow", BAG_DEFAULTS.bagShadow),
    bagCounterBadge: t(
        theme,
        "cart-empty-bag-counter-badge",
        BAG_DEFAULTS.bagCounterBadge,
    ),
    bagTextCounterBadge: t(
        theme,
        "cart-empty-bag-text-counter-badge",
        BAG_DEFAULTS.bagTextCounterBadge,
    ),
    bagStrap: t(theme, "cart-empty-bag-strap", BAG_DEFAULTS.bagStrap),
});

const declinedFromTheme = theme => ({
    declinedInk: t(theme, "black", DECLINED_DEFAULTS.declinedInk),
    declinedMark: t(theme, "btn-primary", DECLINED_DEFAULTS.declinedMark),
    declinedButton: t(theme, "btn-primary", DECLINED_DEFAULTS.declinedButton),
});

/**
 * Shared button + status tokens from Prismic Theme (same for Brand / Gray).
 */
const sharedBrandTokens = theme => {
    const statusBgActive = t(theme, "btn-primary", "#6baa33");
    const statusBgInactive = t(theme, "gray-300", "#bbc1c9");

    return {
        textDiscount: t(theme, "errors", "#cc321b"),
        statusBgActive,
        statusIconActive: t(theme, "label-btn-primary", "#ffffff"),
        // Labels sit on email Background (above icons), not on the status fill.
        statusColorActive: t(theme, "text-color-default", "#2a384e"),
        statusBgInactive,
        statusIconInactive: t(theme, "gray-600", "#37404e"),
        statusColorInactive: t(theme, "gray-600", "#37404e"),
        dotActive: statusBgActive,
        dotInactive: statusBgInactive,
        borderWidthBtn: t(theme, "border-width-btn", "2px"),
        btnRadius: t(theme, "border-radius-btn", "16px"),
        btnShopBg: t(theme, "background-btn-secondary", "#fafbfc"),
        btnShopColor: t(theme, "label-btn-secondary", "#030405"),
        btnShopBorder: t(theme, "btn-secondary", "#fafbfc"),
        btnShopBgHover: t(theme, "background-btn-secondary-hover", "#f2f4f7"),
        btnShopColorHover: t(theme, "label-btn-secondary-hover", "#1b2129"),
        btnShopBorderHover: t(theme, "btn-secondary-hover", "#f2f4f7"),
        btnTrackBg: t(theme, "btn-primary", "#6baa33"),
        btnTrackColor: t(theme, "label-btn-primary", "#ffffff"),
        btnTrackBorder: t(theme, "border-btn-primary", "#6baa33"),
        btnTrackBgHover: t(theme, "btn-primary-hover", "#6baa33"),
        btnTrackColorHover: t(theme, "label-btn-primary-hover", "#ffffff"),
        btnTrackBorderHover: t(theme, "border-btn-primary-hover", "#6baa33"),
        ...linkTokensFromTheme(theme),
        ...bagFromTheme(theme),
        ...declinedFromTheme(theme),
    };
};

/**
 * Parse Theme `variables` SCSS into a flat map of TLP keys (no leading `$`).
 * Resolves `$other-var` references; skips map/list values.
 *
 * @param {string} scss
 * @returns {Record<string, string>}
 */
export function parseScssThemeVariables(scss) {
    if (!scss || typeof scss !== "string") return {};

    /** @type {Record<string, string>} */
    const raw = {};
    const declRe = /\$([a-zA-Z0-9_-]+)\s*:\s*([^;]+);/g;
    let match = declRe.exec(scss);
    while (match) {
        const key = match[1];
        let value = match[2]
            .replace(/\/\/[^\n]*/g, "")
            .replace(/\/\*[\s\S]*?\*\//g, "")
            .trim();
        if (value.startsWith("(")) {
            match = declRe.exec(scss);
            continue;
        }
        if (value) raw[key] = value;
        match = declRe.exec(scss);
    }

    /**
     * @param {string} key
     * @param {Set<string>} seen
     * @returns {string | undefined}
     */
    const resolve = (key, seen = new Set()) => {
        if (!(key in raw) || seen.has(key)) return undefined;
        seen.add(key);
        const value = raw[key];
        const pureRef = value.match(/^\$([a-zA-Z0-9_-]+)$/);
        if (pureRef) {
            return resolve(pureRef[1], seen) ?? value;
        }
        return value.replace(/\$([a-zA-Z0-9_-]+)/g, (full, refKey) => {
            const resolved = resolve(refKey, new Set(seen));
            return resolved != null ? resolved : full;
        });
    };

    /** @type {Record<string, string>} */
    const out = {};
    for (const key of Object.keys(raw)) {
        const value = resolve(key);
        if (value != null && value !== "" && value !== "none") {
            out[key] = value;
        }
    }
    return out;
}

/**
 * @typedef {'prismic' | 'mock'} ThemeTokenSource
 * @typedef {{
 *   tokens: typeof MOCK_PRISMIC_THEME & Record<string, string>,
 *   source: ThemeTokenSource,
 *   reason?: 'missing_endpoint' | 'empty_theme' | 'parse_empty' | 'fetch_error',
 * }} StoreThemeTokensResult
 */

/**
 * Fetch Theme from store.prismic_api_endpoint and map SCSS → TLP keys.
 * Always returns a usable token map (mock merge / full mock on failure).
 *
 * @param {string | null | undefined} prismicApiEndpoint
 * @returns {Promise<StoreThemeTokensResult>}
 */
export async function fetchStoreThemeTokens(prismicApiEndpoint) {
    if (!prismicApiEndpoint) {
        return {
            tokens: { ...MOCK_PRISMIC_THEME },
            source: "mock",
            reason: "missing_endpoint",
        };
    }

    try {
        const scss = await getPrismicThemeVariables(prismicApiEndpoint);
        if (!scss) {
            return {
                tokens: { ...MOCK_PRISMIC_THEME },
                source: "mock",
                reason: "empty_theme",
            };
        }

        const parsed = parseScssThemeVariables(scss);
        if (!Object.keys(parsed).length) {
            return {
                tokens: { ...MOCK_PRISMIC_THEME },
                source: "mock",
                reason: "parse_empty",
            };
        }

        return {
            tokens: { ...MOCK_PRISMIC_THEME, ...parsed },
            source: "prismic",
        };
    } catch (error) {
        console.error("fetchStoreThemeTokens failed:", error);
        return {
            tokens: { ...MOCK_PRISMIC_THEME },
            source: "mock",
            reason: "fetch_error",
        };
    }
}

/**
 * Resolve Brand / Gray / Dark token maps for {{THEME.*}} slots.
 * @param {"brand"|"gray"|"dark"} skinId
 * @param {typeof MOCK_PRISMIC_THEME} [prismicTheme]
 */
export function resolveSkinTokens(skinId, prismicTheme = MOCK_PRISMIC_THEME) {
    const theme = { ...MOCK_PRISMIC_THEME, ...prismicTheme };
    const shared = sharedBrandTokens(theme);

    if (skinId === "gray") {
        const textColor = t(theme, "text-color-default", "#2a384e");
        const textColorMuted = t(theme, "gray-600", "#37404e");
        const backgroundAccent = t(theme, "gray-100", "#d8dce3");
        return {
            background: t(theme, "background-theme", "#ffffff"),
            backgroundAccent,
            headerBackground: backgroundAccent,
            footerBackground: backgroundAccent,
            textColor,
            textDescription: t(theme, "text-color-default", "#2a384e"),
            textColorMuted,
            headingText: t(theme, "heading-color", "#37404e"),
            separator: t(theme, "gray-400", "#858f9d"),
            ...shared,
            footerTextColor: textColorMuted,
            footerIconColor: textColor,
        };
    }

    if (skinId === "dark") {
        const statusBgActive = t(theme, "btn-primary", "#6baa33");
        const backgroundAccent = "#1a1a1a";
        return {
            background: "#0d0d0d",
            backgroundAccent,
            headerBackground: backgroundAccent,
            footerBackground: backgroundAccent,
            textColor: "#ffffff",
            textDescription: "#ffffff",
            textColorMuted: "#e6e6e6",
            headingText: "#ffffff",
            textDiscount: t(theme, "errors", "#cc321b"),
            separator: "#333333",
            statusBgActive,
            statusIconActive: "#ffffff",
            statusColorActive: "#ffffff",
            statusBgInactive: "#333333",
            statusIconInactive: "#e6e6e6",
            statusColorInactive: "#e6e6e6",
            dotActive: statusBgActive,
            dotInactive: "#333333",
            borderWidthBtn: t(theme, "border-width-btn", "1px"),
            btnRadius: t(theme, "border-radius-btn", "16px"),
            btnShopBg: t(theme, "background-btn-secondary", "#fafbfc"),
            btnShopColor: t(theme, "label-btn-secondary", "#030405"),
            btnShopBorder: t(theme, "btn-secondary", "#fafbfc"),
            btnShopBgHover: t(
                theme,
                "background-btn-secondary-hover",
                "#f2f4f7",
            ),
            btnShopColorHover: t(theme, "label-btn-secondary-hover", "#1b2129"),
            btnShopBorderHover: t(theme, "btn-secondary-hover", "#f2f4f7"),
            btnTrackBg: t(theme, "btn-primary", "#6baa33"),
            btnTrackColor: t(theme, "label-btn-primary", "#ffffff"),
            btnTrackBorder: t(theme, "border-btn-primary", "#6baa33"),
            btnTrackBgHover: t(theme, "btn-primary-hover", "#6baa33"),
            btnTrackColorHover: t(theme, "label-btn-primary-hover", "#ffffff"),
            btnTrackBorderHover: t(
                theme,
                "border-btn-primary-hover",
                "#6baa33",
            ),
            colorLink: "#ffffff",
            colorLinkHover: "#e6e6e6",
            footerTextColor: "#e6e6e6",
            footerIconColor: "#ffffff",
            ...bagFromTheme(theme),
            ...declinedFromTheme(theme),
        };
    }

    // brand (default)
    const textColor = t(theme, "text-color-default", "#2a384e");
    const textColorMuted = t(theme, "gray-600", "#37404e");
    const backgroundAccent = t(theme, "primary-200", "#d8dce3");
    return {
        background: t(theme, "background-theme", "#ffffff"),
        backgroundAccent,
        headerBackground: backgroundAccent,
        footerBackground: backgroundAccent,
        textColor,
        textDescription: t(theme, "text-color-default", "#2a384e"),
        textColorMuted,
        headingText: t(theme, "heading-color", "#37404e"),
        separator: t(theme, "gray-100", "#f2f4f7"),
        ...shared,
        footerTextColor: textColorMuted,
        footerIconColor: textColor,
    };
}

/**
 * Fill {{THEME.token}} slots in a base email HTML string.
 * Does not touch Go placeholders ({{.StoreLogo}}, {{ if }}, etc.).
 */
export function applyThemeToTemplate(baseHtml, tokens) {
    if (!baseHtml) return "";
    return baseHtml.replace(/\{\{THEME\.(\w+)\}\}/g, (match, key) => {
        const value = tokens?.[key];
        return value != null && value !== "" ? value : match;
    });
}

/** Keys still present as `{{THEME.*}}` after theming (must be empty before save). */
export function collectUnresolvedThemeSlots(html) {
    const keys = [];
    if (!html) return keys;
    const re = /\{\{THEME\.([A-Za-z0-9_]+)\}\}/g;
    let match;
    while ((match = re.exec(html)) !== null) {
        keys.push(match[1]);
    }
    return keys;
}

/**
 * Guard for Apply/save: HTML must not contain theme slots that Go would
 * mis-parse (or fail on) at send time. Preserves Go `{{.Var}}` / `{{ if }}`.
 *
 * @param {string} html
 * @returns {string} same html when safe
 */
export function assertEmailHtmlSafeToSave(html) {
    const body = String(html || "");
    if (!body.trim()) {
        throw new Error("Themed email HTML is empty.");
    }

    const unresolved = collectUnresolvedThemeSlots(body);
    if (unresolved.length) {
        const unique = [...new Set(unresolved)];
        const preview = unique.slice(0, 12).join(", ");
        const more = unique.length > 12 ? ` (+${unique.length - 12} more)` : "";
        throw new Error(
            `Theme apply left unresolved {{THEME.*}} slots: ${preview}${more}. Theme was not saved.`,
        );
    }

    // Malformed leftovers (e.g. {{THEME.foo without closing braces).
    if (/\{\{\s*THEME\b/i.test(body)) {
        throw new Error(
            "Theme apply left malformed {{THEME…}} markup. Theme was not saved.",
        );
    }

    return body;
}

/**
 * Admin preview cannot run Go templates. Pick one OrderType branch so
 * `{{ if eq .OrderType "DELIVERY" }}…{{ end }} {{ if … "PICKUP" }}…{{ end }}`
 * does not leak as visible text or render both trackers.
 *
 * Apply keeps the full Go markup for the backend.
 *
 * @param {string} html
 * @param {'DELIVERY' | 'PICKUP'} [orderType]
 */
export function resolveOrderTypeBranchesForPreview(
    html,
    orderType = "DELIVERY",
) {
    if (!html) return "";
    const pick =
        String(orderType || "DELIVERY").toUpperCase() === "PICKUP"
            ? "PICKUP"
            : "DELIVERY";

    return String(html).replace(
        /\{\{\s*if\s+eq\s+\.OrderType\s+"DELIVERY"\s*\}\}([\s\S]*?)\{\{\s*end\s*\}\}\s*\{\{\s*if\s+eq\s+\.OrderType\s+"PICKUP"\s*\}\}([\s\S]*?)\{\{\s*end\s*\}\}/g,
        (_full, deliveryBranch, pickupBranch) =>
            pick === "PICKUP" ? pickupBranch : deliveryBranch,
    );
}

export { FILL_TOKEN_KEYS, TEXT_TOKEN_KEYS };

const HEX_IN_CSS_RE = /#([0-9a-fA-F]{3}|[0-9a-fA-F]{6})\b/;

function normalizeHexToken(input) {
    const raw = String(input || "").trim();
    const m = raw.match(/^#?([0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/);
    if (!m) return null;
    let h = m[1];
    if (h.length === 3) {
        h = h
            .split("")
            .map(c => c + c)
            .join("");
    }
    return `#${h.toLowerCase()}`;
}

function extractSolidFromCss(css) {
    const asHex = normalizeHexToken(css);
    if (asHex) return asHex;
    const m = String(css || "").match(HEX_IN_CSS_RE);
    if (!m) return "#000000";
    return normalizeHexToken(m[0]) || "#000000";
}

/**
 * Rich CSS value + solid hex fallback (first stop / same hex).
 * @param {string} cssOrHex
 * @returns {{ css: string, solid: string }}
 */
export function toThemePair(cssOrHex) {
    const raw = String(cssOrHex || "").trim();
    const solid = extractSolidFromCss(raw);
    if (/gradient\(/i.test(raw)) {
        return { css: raw, solid };
    }
    const hex = normalizeHexToken(raw);
    if (hex) return { css: hex, solid: hex };
    return { css: raw || solid, solid };
}

/** Valid background-image for text-clip when the token is a solid hex. */
function textFillBackgroundImage(css, solid) {
    if (/gradient\(/i.test(css)) return css;
    return `linear-gradient(0deg, ${solid} 0%, ${solid} 100%)`;
}

/**
 * Expand skin/override tokens into email-safe pairs:
 * - fills → `X` (rich) + `XSolid` (hex)
 * - text → `X` (hex for color:) + `XFill` (gradient or flat gradient for clip)
 */
export function expandThemeTokenPairs(tokens) {
    if (!tokens || typeof tokens !== "object") return {};
    const out = { ...tokens };

    for (const key of FILL_TOKEN_KEYS) {
        if (out[key] == null || out[key] === "") continue;
        const { css, solid } = toThemePair(out[key]);
        out[key] = css;
        out[`${key}Solid`] = solid;
    }

    for (const key of TEXT_TOKEN_KEYS) {
        if (out[key] == null || out[key] === "") continue;
        const { css, solid } = toThemePair(out[key]);
        out[key] = solid;
        out[`${key}Fill`] = textFillBackgroundImage(css, solid);
    }

    return out;
}
